// stepper.service.ts
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { IStep, IStepState } from './step.interface';

import { createStore } from '@ngneat/elf';
import {
  getEntity,
  selectEntity,
  updateEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import { environment } from 'src/environments/environment';

const STORE_NAME = 'stepper';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: environment.cacheDbName,
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },

  withEntities<IStepState, 'id'>({ idKey: 'id' }),
  // withProps<IScrollPosition>({ x: 0, y: 0 }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class StepperService {
  // private _currentStepIndices$ = new BehaviorSubject<{ [key: string]: number }>(
  //   {},
  // );

  // private _currentSteps$ = new BehaviorSubject<{
  //   [key: string]: IStep | undefined;
  // }>({});

  constructor() {}

  getSteps(id: string): IStep[] {
    return store.query(getEntity(id))?.steps || [];
  }

  public getCurrentStepIndex(id: string): number {
    const entity = store.query(getEntity(id));
    return entity ? entity.currentStepIndex : 0;
  }

  getCurrentStep(id: string): IStep | undefined {
    const entity = store.query(getEntity(id));

    if (entity) {
      const currentStepIndex = entity.currentStepIndex || 0;
      return entity.steps[currentStepIndex];
    }
    return undefined;
  }

  // Expose observable for currentStepIndex for a specific ID
  getCurrentStepIndex$(id: string): Observable<number> {
    return store
      .pipe(selectEntity(id, { pluck: 'currentStepIndex' }))
      .pipe(
        map((currentStepIndex: number | undefined) => currentStepIndex || 0),
      );
  }

  // Expose observable for currentStep for a specific ID
  getCurrentStep$(id: string): Observable<IStep | undefined> {
    return store.pipe(
      selectEntity(id, { pluck: (e) => e.steps[e.currentStepIndex] }),
    );
  }

  changeStep(id: string, index: number): void {
    store.update(
      updateEntities(id, (state) => {
        console.log('id: ', id, 'Updating steps: ', state);
        
        if (
          index >= 0 &&
          index < state.steps?.length &&
          !state.steps[index]?.disabled
        ) {
          return { ...state, currentStepIndex: index };
        }

        return state;
      }),
    );
  }

  nextStep(id: string): void {
    const currentStepIndex = store.query(getEntity(id))?.currentStepIndex || 0;
    this.changeStep(id, currentStepIndex + 1);
  }

  previousStep(id: string): void {
    const currentStepIndex = store.query(getEntity(id))?.currentStepIndex || 0;
    this.changeStep(id, currentStepIndex - 1);
  }

  setSteps(id: string, steps: IStep[]): void {
    const existingState = store.query(getEntity(id));
    const state: IStepState = {
      id,
      steps: existingState ? existingState.steps : steps,
      currentStepIndex: existingState ? existingState.currentStepIndex : 0,
    };
    store.update(upsertEntities(state));
  }

  completeCurrentStep(id: string): void {
    const currentStepIndex = store.query(getEntity(id))?.currentStepIndex || 0;
    this.completeStep(id, currentStepIndex);
  }

  completeStep(id: string, index?: number): void {
    store.update(
      updateEntities(id, (state: IStepState) => {
        const stepIndex = index ? index : state.currentStepIndex || 0;
        const steps = state.steps;
        const step = steps[stepIndex];

        if (step) {
          // Mark the current step as completed
          step.completed = true;
          return { ...state, steps };
        }

        return state;
      }),
    );
  }

  enableNextStep(id: string): void {
    const currentStepIndex = store.query(getEntity(id))?.currentStepIndex || 0;
    this.enableStep(id, currentStepIndex + 1);
  }

  enableStep(id: string, index: number): void {
    store.update(
      updateEntities(id, (state) => {
        const stepIndex = index || 0;
        const steps = state.steps;
        const step = steps[stepIndex];

        if (step) {
          // Mark the current step as completed
          step.disabled = false;
          return { ...state, steps };
        }
        return state;
      }),
    );
  }
}
